/**
 * DCS CDN Image Plugin for Vite
 *
 * Rewrites local static image references (e.g. `/images/staff/photo.jpg`)
 * to CDN URLs at build time using the `.dcs/cdn-image-map.json` mapping file
 * generated by the `image-migrate adopt` CLI command.
 *
 * For raster images with WebP variants, `<img>` elements are transformed into
 * responsive `<picture>` elements with `srcset` for optimised delivery.
 * SVGs receive a simple URL swap with no variant handling.
 *
 * **In development mode this plugin is a no-op** — local `/images/` paths
 * continue to work via Vite's static asset serving so hot-reload is unaffected.
 *
 * The plugin handles two in-pipeline replacement vectors:
 *
 * 1. **Module transform** (`transform` hook) — rewrites `<img>` tags and
 *    string literals in Vue SFCs, TS, JS, CSS, MD, and HTML modules.
 * 2. **Chunk rendering** (`renderChunk` hook) — rewrites image paths in
 *    final rendered JS/CSS chunks AFTER Vite's `define` substitution.
 *    This catches data injected by `dcsContentPlugin` via `__DCS_CONTENT__`
 *    (from `.dcs/content.yaml`) which bypasses the `transform` hook.
 *
 * A third vector — **post-build file processing** — is handled by the
 * companion `dcsCdnBuildEnd` hook, which must be registered separately in
 * VitePress config. VitePress generates HTML *after* both Vite builds
 * complete, so Vite plugin hooks (`closeBundle`, `transformIndexHtml`)
 * cannot catch SSR-rendered `<head>` tags or static `public/` files like
 * `service-worker.js`.
 *
 * @example
 * ```ts
 * // .vitepress/config.ts
 * import { dcsCdnImagePlugin, dcsCdnBuildEnd } from '@duffcloudservices/cms/plugins'
 *
 * export default defineConfig({
 *   vite: {
 *     plugins: [
 *       dcsCdnImagePlugin()
 *     ]
 *   },
 *   buildEnd: dcsCdnBuildEnd()
 * })
 * ```
 */

import fs from 'node:fs'
import path from 'node:path'
import type { Plugin } from 'vite'

// ────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────

export interface CdnImageMapVariant {
  suffix: string
  blobPath: string
  cdnUrl: string
  width: number
  height: number
  size: number
}

export interface CdnImageMapEntry {
  localPath: string
  blobPath: string
  cdnUrl: string
  fileName: string
  contentType: string
  originalSize: number
  width?: number
  height?: number
  hash: string
  variants?: CdnImageMapVariant[]
}

export interface CdnImageMap {
  generated: string
  site: string
  cdnBase: string
  images: CdnImageMapEntry[]
}

export interface DcsCdnImagePluginOptions {
  /** Path to cdn-image-map.json relative to project root (default: '.dcs/cdn-image-map.json') */
  mapPath?: string

  /**
   * Patterns to match for replacement. Each must be a **leading-slash path prefix**
   * that appears in source code (e.g. `/images/`). The `localPath` field in the
   * mapping file is compared *without* a leading slash.
   *
   * Default: `['/images/']`
   */
  pathPrefixes?: string[]

  /**
   * Default `sizes` attribute for responsive `<picture>` elements.
   * Override per-context via the `data-sizes` attribute on the original `<img>`.
   *
   * Default: `'(max-width: 1024px) 100vw, 1024px'`
   */
  defaultSizes?: string

  /** Enable debug logging */
  debug?: boolean
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

/**
 * Build the ` width="W" height="H"` attribute fragment for a map entry — the
 * CLS layout-shift hint (Phase 1). Dimensions are read from the entry's
 * intrinsic `width`/`height`; they are aspect-ratio hints, so emitting the
 * source dimensions on an `<img>` whose `src` is a resized variant is correct
 * (the ratio is scale-invariant and CSS still controls the rendered size).
 *
 * Emitted ONLY when both are known and strictly positive. Older image-map
 * entries carry no top-level dimensions (and variant `height` is often `0`) —
 * in that case NO hint is emitted rather than a fabricated/wrong ratio.
 */
function dimensionAttrs(entry: CdnImageMapEntry): string {
  const w = entry.width
  const h = entry.height
  if (typeof w === 'number' && w > 0 && typeof h === 'number' && h > 0) {
    return ` width="${w}" height="${h}"`
  }
  return ''
}

/**
 * Build a responsive `<picture>` element for a raster image that has
 * WebP variants.
 */
function buildPictureElement(
  entry: CdnImageMapEntry,
  alt: string,
  extraAttrs: string,
  sizes: string
): string {
  const variants = entry.variants ?? []
  const dims = dimensionAttrs(entry)

  // Collect only the display-relevant variants (not thumb, not og)
  const displayVariants = variants.filter(
    (v) => v.suffix === '-sm' || v.suffix === '-md' || v.suffix === '-lg'
  )

  if (displayVariants.length === 0) {
    // No display variants — fall back to original CDN URL as plain <img>
    return `<img src="${escapeHtml(entry.cdnUrl)}" alt="${escapeHtml(alt)}"${extraAttrs}${dims} loading="lazy" decoding="async" />`
  }

  const srcset = displayVariants
    .map((v) => `${v.cdnUrl} ${v.width}w`)
    .join(', ')

  // Pick the -md variant as the default src, or fall through to original
  const mdVariant = variants.find((v) => v.suffix === '-md')
  const fallbackSrc = mdVariant ? mdVariant.cdnUrl : entry.cdnUrl

  return [
    '<picture>',
    `  <source srcset="${srcset}" type="image/webp" sizes="${escapeHtml(sizes)}" />`,
    `  <img src="${escapeHtml(fallbackSrc)}" alt="${escapeHtml(alt)}"${extraAttrs}${dims} loading="lazy" decoding="async" />`,
    '</picture>',
  ].join('\n')
}

// ────────────────────────────────────────────────────────────────────────────
// Shared helpers
// ────────────────────────────────────────────────────────────────────────────

/**
 * Try to find and load the CDN image map from common locations.
 * VitePress nests docs under a subfolder, so we check parent dirs too.
 */
function loadCdnImageMap(
  projectRoot: string,
  relativeMapPath: string,
  debug: boolean
): Map<string, CdnImageMapEntry> | null {
  const possiblePaths = [
    path.resolve(projectRoot, relativeMapPath),
    path.resolve(projectRoot, '..', relativeMapPath),
    path.resolve(process.cwd(), relativeMapPath),
  ]

  for (const testPath of possiblePaths) {
    if (fs.existsSync(testPath)) {
      try {
        const raw = fs.readFileSync(testPath, 'utf8')
        const data: CdnImageMap = JSON.parse(raw)

        const map = new Map<string, CdnImageMapEntry>()
        for (const entry of data.images) {
          // Store both with and without leading slash for flexible matching
          map.set(entry.localPath, entry)
          if (!entry.localPath.startsWith('/')) {
            map.set('/' + entry.localPath, entry)
          }
        }

        if (debug) {
          console.log(`[dcs-cdn-image] Loaded ${data.images.length} entries from ${testPath}`)
        }
        return map
      } catch (error) {
        console.warn(`[dcs-cdn-image] Failed to parse ${testPath}:`, error)
      }
    }
  }

  if (debug) {
    console.log('[dcs-cdn-image] No cdn-image-map.json found at:')
    possiblePaths.forEach((p) => console.log(`  - ${p}`))
  }
  return null
}

// ────────────────────────────────────────────────────────────────────────────
// Vite Plugin
// ────────────────────────────────────────────────────────────────────────────

/**
 * Vite plugin that rewrites static `/images/` paths to CDN URLs at build time.
 */
export function dcsCdnImagePlugin(options: DcsCdnImagePluginOptions = {}): Plugin {
  const {
    mapPath = '.dcs/cdn-image-map.json',
    pathPrefixes = ['/images/'],
    defaultSizes = '(max-width: 1024px) 100vw, 1024px',
    debug = false,
  } = options

  // Lookup map built from the JSON file: key = localPath (no leading slash)
  let imageMap: Map<string, CdnImageMapEntry> | null = null
  let isProduction = false

  /**
   * Check whether a source code string contains any of the path prefixes
   * we care about. Quick bail-out for files that don't reference images.
   */
  function hasImageReferences(code: string): boolean {
    return pathPrefixes.some((prefix) => code.includes(prefix))
  }

  /**
   * Replace image path references in any string value with CDN URLs.
   * Used for code transforms and renderChunk post-processing.
   */
  function replaceImagePaths(str: string): string {
    if (!imageMap || imageMap.size === 0) return str
    let result = str
    for (const prefix of pathPrefixes) {
      // Global replace: matches prefix followed by a relative path bounded
      // by quotes, parens, or whitespace. Allows spaces in filenames.
      const regex = new RegExp(
        `(["'\`(])${escapeRegex(prefix)}([^"'\`()]+)`,
        'g'
      )
      result = result.replace(regex, (match, quote, relPath) => {
        const localPath = prefix.replace(/^\//, '') + relPath
        const entry = imageMap!.get(localPath) || imageMap!.get('/' + localPath)
        if (!entry) return match
        return `${quote}${entry.cdnUrl}`
      })
    }
    return result
  }

  /**
   * Replace standalone URL references in JS/TS string literals and Vue template
   * attributes. This handles patterns like:
   *   src="/images/foo.jpg"
   *   { src: '/images/foo.jpg' }
   *   url(/images/foo.svg)
   *
   * For `<img>` tags with raster images that have variants, the entire tag is
   * replaced with a `<picture>` element.
   */
  function transformCode(code: string): string {
    if (!imageMap || imageMap.size === 0) return code

    // ── Pass 1: Replace <img> tags that reference mapped raster images ──────
    // Matches:  <img ... src="/images/foo.jpg" ... />  or  <img ... src="/images/foo.jpg" ... >
    const imgTagRegex = /<img\b([^>]*?)src=["'](\/(images\/[^"']+))["']([^>]*?)\/?>/gi
    code = code.replace(imgTagRegex, (_match, beforeSrc, _srcWithSlash, localPath, afterSrc) => {
      const entry = imageMap!.get(localPath) || imageMap!.get('/' + localPath)
      if (!entry) return _match

      // Extract alt from the existing attributes
      const altMatch = (beforeSrc + afterSrc).match(/alt=["']([^"']*)["']/)
      const alt = altMatch ? altMatch[1] : ''

      // Collect other attributes (not src, alt, loading, decoding — we set those)
      let otherAttrs = (beforeSrc + afterSrc)
        .replace(/\balt=["'][^"']*["']/gi, '')
        .replace(/\bloading=["'][^"']*["']/gi, '')
        .replace(/\bdecoding=["'][^"']*["']/gi, '')
        .trim()

      // When the map entry carries intrinsic dimensions, dimensionAttrs() will
      // emit width/height — strip any hand-authored ones (quoted or bare) so the
      // output doesn't carry DUPLICATE width/height attributes (a Vue compiler
      // duplicate-attribute error). Without entry dims, keep the author's.
      if (dimensionAttrs(entry)) {
        otherAttrs = otherAttrs
          .replace(/\b(?:width|height)=(?:"[^"]*"|'[^']*'|[^\s"'>/]+)/gi, '')
          .replace(/\s{2,}/g, ' ')
          .trim()
      }

      const extraAttrs = otherAttrs ? ' ' + otherAttrs : ''

      // Extract custom sizes if specified
      const sizesMatch = (beforeSrc + afterSrc).match(/data-sizes=["']([^"']*)["']/)
      const sizes = sizesMatch ? sizesMatch[1] : defaultSizes

      // SVGs and images without variants → simple URL swap <img>
      if (!entry.variants || entry.variants.length === 0) {
        return `<img src="${escapeHtml(entry.cdnUrl)}" alt="${escapeHtml(alt)}"${extraAttrs}${dimensionAttrs(entry)} loading="lazy" decoding="async" />`
      }

      // Raster with variants → <picture>
      return buildPictureElement(entry, alt, extraAttrs, sizes)
    })

    // ── Pass 2: Replace remaining string literal / CSS url() references ─────
    code = replaceImagePaths(code)

    return code
  }

  return {
    name: 'dcs-cdn-image',
    enforce: 'pre',

    configResolved(config) {
      isProduction = config.command === 'build'
      if (!isProduction) {
        if (debug) {
          console.log('[dcs-cdn-image] Dev mode — plugin disabled (local images served by Vite)')
        }
        return
      }
      imageMap = loadCdnImageMap(config.root, mapPath, debug)
    },

    transform(code, id) {
      // Only run in production builds
      if (!isProduction || !imageMap) return

      // Only process relevant file types
      if (!/\.(vue|ts|tsx|js|jsx|css|scss|md|html)(\?.*)?$/.test(id)) return

      // Quick bail — skip files with no image path references
      if (!hasImageReferences(code)) return

      const transformed = transformCode(code)
      if (transformed !== code) {
        return { code: transformed, map: null }
      }
    },

    /**
     * Replace image paths in final rendered JS/CSS chunks.
     *
     * This fires AFTER Vite's `define` substitution, catching paths that were
     * injected via `define` values (e.g. `__DCS_CONTENT__` set by
     * `dcsContentPlugin` from `.dcs/content.yaml`). Those paths bypass the
     * `transform` hook because `define` replacement happens at bundle time.
     */
    renderChunk(code, _chunk) {
      if (!isProduction || !imageMap) return null
      if (!hasImageReferences(code)) return null

      const replaced = replaceImagePaths(code)
      if (replaced !== code) {
        if (debug) {
          console.log(`[dcs-cdn-image] renderChunk: rewrote image paths in ${_chunk.fileName}`)
        }
        return { code: replaced, map: null }
      }
      return null
    },

    /**
     * Transform HTML output to catch references injected outside the module
     * pipeline — e.g. VitePress `head` config tags (favicons, OG images).
     *
     * Note: In VitePress, this hook fires for the `index.html` template but
     * NOT for SSR-rendered per-page HTML. Use `dcsCdnBuildEnd` for full
     * coverage of generated HTML files.
     */
    transformIndexHtml(html) {
      if (!isProduction || !imageMap) return html
      if (!hasImageReferences(html)) return html

      // Replace href/src/content attributes that reference mapped images
      for (const prefix of pathPrefixes) {
        const attrRegex = new RegExp(
          `((?:href|src|content)=["'])${escapeRegex(prefix)}([^"']+)(["'])`,
          'gi'
        )
        html = html.replace(attrRegex, (match, pre, relPath, post) => {
          const localPath = prefix.replace(/^\//, '') + relPath
          const entry = imageMap!.get(localPath) || imageMap!.get('/' + localPath)
          if (!entry) return match
          return `${pre}${entry.cdnUrl}${post}`
        })
      }

      return html
    },
  }
}

/**
 * Escape a string for use in a RegExp.
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

// ────────────────────────────────────────────────────────────────────────────
// VitePress buildEnd hook
// ────────────────────────────────────────────────────────────────────────────

export interface DcsCdnBuildEndOptions {
  /** Path to cdn-image-map.json relative to project root (default: '.dcs/cdn-image-map.json') */
  mapPath?: string

  /**
   * Patterns to match for replacement.
   * Default: `['/images/']`
   */
  pathPrefixes?: string[]

  /** Enable debug logging */
  debug?: boolean

  /**
   * File extensions to process in the output directory.
   * Default: `['.html', '.js']`
   */
  extensions?: string[]
}

/**
 * VitePress `buildEnd` hook factory that post-processes generated HTML and
 * static files in the output directory to replace remaining `/images/` paths
 * with CDN URLs.
 *
 * VitePress generates HTML **after** both Vite builds complete, so Vite
 * plugin hooks (`closeBundle`, `transformIndexHtml`) cannot catch
 * SSR-rendered `<head>` tags (favicons, OG images). This hook runs after all
 * HTML files are written to disk.
 *
 * Also rewrites static files (e.g. `service-worker.js`) copied from
 * `public/` that are not part of Vite's module pipeline.
 *
 * @example
 * ```ts
 * // .vitepress/config.ts
 * import { dcsCdnBuildEnd } from '@duffcloudservices/cms/plugins'
 *
 * export default defineConfig({
 *   buildEnd: dcsCdnBuildEnd({ debug: true })
 * })
 * ```
 */
export function dcsCdnBuildEnd(options: DcsCdnBuildEndOptions = {}) {
  const {
    mapPath = '.dcs/cdn-image-map.json',
    pathPrefixes = ['/images/'],
    debug = false,
    extensions = ['.html', '.js'],
  } = options

  return async (siteConfig: { root: string; outDir: string }) => {
    const imageMap = loadCdnImageMap(siteConfig.root, mapPath, debug)
    if (!imageMap) {
      if (debug) {
        console.log('[dcs-cdn-image] buildEnd: no image map found')
      }
      return
    }

    const outDir = siteConfig.outDir
    if (!fs.existsSync(outDir)) return

    let filesProcessed = 0
    let refsReplaced = 0

    function walkDir(dir: string) {
      const entries = fs.readdirSync(dir, { withFileTypes: true })
      for (const entry of entries) {
        const fullPath = path.join(dir, entry.name)
        if (entry.isDirectory()) {
          walkDir(fullPath)
        } else if (extensions.some((ext) => entry.name.endsWith(ext))) {
          processFile(fullPath)
        }
      }
    }

    function processFile(filePath: string) {
      let content = fs.readFileSync(filePath, 'utf8')
      if (!pathPrefixes.some((p) => content.includes(p))) return

      let changed = false

      if (filePath.endsWith('.html')) {
        // HTML: replace href/src/content attribute values
        for (const prefix of pathPrefixes) {
          const attrRegex = new RegExp(
            `((?:href|src|content)=["'])${escapeRegex(prefix)}([^"']+)(["'])`,
            'gi'
          )
          content = content.replace(attrRegex, (match, pre, relPath, post) => {
            const localPath = prefix.replace(/^\//, '') + relPath
            const mapEntry = imageMap!.get(localPath) || imageMap!.get('/' + localPath)
            if (!mapEntry) return match
            changed = true
            refsReplaced++
            return `${pre}${mapEntry.cdnUrl}${post}`
          })
        }
      } else {
        // JS/other: replace quoted string paths
        for (const prefix of pathPrefixes) {
          const regex = new RegExp(
            `(["'\`(])${escapeRegex(prefix)}([^"'\`()]+)`,
            'g'
          )
          content = content.replace(regex, (match, quote, relPath) => {
            const localPath = prefix.replace(/^\//, '') + relPath
            const entry = imageMap!.get(localPath) || imageMap!.get('/' + localPath)
            if (!entry) return match
            changed = true
            refsReplaced++
            return `${quote}${entry.cdnUrl}`
          })
        }
      }

      if (changed) {
        fs.writeFileSync(filePath, content, 'utf8')
        filesProcessed++
      }
    }

    walkDir(outDir)

    if (debug) {
      console.log(
        `[dcs-cdn-image] buildEnd: post-processed ${filesProcessed} files, replaced ${refsReplaced} references`
      )
    }
  }
}